import { Text } from '@/components/atoms/text';
import { Flex } from '@/styles/globalStyles';
import { OptionButton, OptionsContainer, TitleContainer } from './styles';
import { useState, useEffect, useRef } from 'react';
import { OpportunityAndRisk } from '@/types/opportunityAndRisk';
import { opportunityAndRisk } from '@/utils/functions/opportunityAndRisk';
import { useTheme } from '@mui/material/styles';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { IconButton } from '@/components/atoms/Button';
import { InfoOutlined } from '@mui/icons-material';
import { ClickAwayListener, Tooltip } from '@mui/material';
import { useTranslation } from '@/hooks/useTranslation';

interface ScoreFilterProps {
  title: string;
  options: OpportunityAndRisk[];
  filteredOptions?: OpportunityAndRisk[];
  variant?: 'risk' | 'opportunity';
  disabled?: boolean;
  onSelectedOptions: (options: OpportunityAndRisk[]) => void;
}

export const ScoreFilter = ({
  title,
  options,
  filteredOptions,
  variant,
  disabled,
  onSelectedOptions,
}: ScoreFilterProps) => {
  const theme = useTheme();
  const onSelectedOptionsRef = useRef(onSelectedOptions);
  const { t } = useTranslation();

  const [openTooltip, setOpenTooltip] = useState(false);

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleTooltipOpen = () => {
    setOpenTooltip(true);
  };

  // Atualiza a ref sempre que a função mudar
  useEffect(() => {
    onSelectedOptionsRef.current = onSelectedOptions;
  }, [onSelectedOptions]);

  const [selectedOptions, setSelectedOptions] = useState<OpportunityAndRisk[]>(
    filteredOptions && filteredOptions.length > 0 ? filteredOptions : [OpportunityAndRisk.ALL]
  );

  // Atualizar selectedOptions quando filteredOptions mudar (ex: ao limpar filtros)
  useEffect(() => {
    if (filteredOptions && filteredOptions.length > 0) {
      // Only update if the arrays are actually different
      setSelectedOptions((current) => {
        if (JSON.stringify(current) !== JSON.stringify(filteredOptions)) {
          return filteredOptions;
        }
        return current;
      });
    } else if (filteredOptions === undefined) {
      // Only update if not already set to ALL
      setSelectedOptions((current) => {
        if (current.length !== 1 || current[0] !== OpportunityAndRisk.ALL) {
          return [OpportunityAndRisk.ALL];
        }
        return current;
      });
    }
  }, [filteredOptions]);

  const buttonStyles =
    variant === 'risk'
      ? {
          backgroundColor: theme.palette.common.white,
          border: `1px solid ${theme.palette.action.active}`,
          borderColor: theme.palette.orange[300],
          color: theme.palette.orange[900],
          '&:hover': {
            backgroundColor: theme.palette.orange[100],
            borderColor: theme.palette.orange[300],
          },
          '&:focus': {
            borderColor: theme.palette.orange[300],
            boxShadow: `0 0 0 4px ${theme.palette.orange[100]}`,
          },
        }
      : {
          '&:hover': {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.light,
          },
        };

  const selectedButtonStyles =
    variant === 'risk'
      ? {
          backgroundColor: theme.palette.orange[700],
          color: theme.palette.common.white,
        }
      : {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.primary,
        };

  const handleSelectOption = (option: OpportunityAndRisk) => {
    let newSelectedOptions: OpportunityAndRisk[];

    if (option === OpportunityAndRisk.ALL) {
      newSelectedOptions = [OpportunityAndRisk.ALL];
    } else {
      if (selectedOptions.includes(option)) {
        newSelectedOptions = selectedOptions.filter((o) => o !== option);
        if (newSelectedOptions.length === 0) {
          newSelectedOptions = [OpportunityAndRisk.ALL];
        }
      } else {
        newSelectedOptions = [
          ...selectedOptions.filter((o) => o !== OpportunityAndRisk.ALL),
          option,
        ];
      }
    }

    setSelectedOptions(newSelectedOptions);
  };

  // Use a ref to track the previous selectedOptions to avoid unnecessary callbacks
  const prevSelectedOptionsRef = useRef<OpportunityAndRisk[]>([]);

  useEffect(() => {
    // Only call the callback if selectedOptions actually changed
    if (JSON.stringify(prevSelectedOptionsRef.current) !== JSON.stringify(selectedOptions)) {
      prevSelectedOptionsRef.current = selectedOptions;
      onSelectedOptionsRef.current(selectedOptions);
    }
  }, [selectedOptions]);

  return (
    <Flex direction="column" gap={1.5} fullWidth>
      <TitleContainer>
        <Text variant="subtitle2" color="text.secondary">
          <SafeMarkdown text={title} />
        </Text>
        {disabled && (
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <div>
              <Tooltip
                onClose={handleTooltipClose}
                open={openTooltip}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title={
                  variant === 'risk'
                    ? t('label.label.no_risk_calculated')
                    : t('label.label.no_opportunity_calculated')
                }
                arrow
                placement="top"
                componentsProps={{
                  popper: {
                    disablePortal: true,
                    style: { textAlign: 'center' },
                  },
                }}
              >
                <IconButton sx={{ padding: 0 }} onClick={handleTooltipOpen}>
                  <InfoOutlined color="action" sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </div>
          </ClickAwayListener>
        )}
      </TitleContainer>

      <OptionsContainer>
        {options.map((option, index) => (
          <OptionButton
            key={index}
            onClick={() => handleSelectOption(option)}
            disabled={disabled}
            variant={selectedOptions.includes(option) ? 'contained' : 'outlined'}
            sx={{
              ...buttonStyles,
              ...(selectedOptions.includes(option) && {
                ...selectedButtonStyles,
                border: `1px solid ${theme.palette.primary.dark}`,
              }),
            }}
          >
            <SafeMarkdown text={opportunityAndRisk.normalize(option, variant)} />
          </OptionButton>
        ))}
      </OptionsContainer>
    </Flex>
  );
};
